import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import throttle from 'lodash/throttle';
import { useIsMounted } from '@wix/da-hooks/pkg/useIsMounted';
const SCROLL_THROTTLE_TIMEOUT = 400;
export function usePopoverOnInteraction({ listenOnClicks, listenOnHover, hideOnScroll = true, hoverDelay = 1, onVisible, onHide }) {
    const [isVisible, setIsVisible] = useState(false);
    const isMounted = useIsMounted();
    const hoverTimerId = useRef();
    const hideTimerId = useRef();
    const wasOpenedByClick = useRef(false);
    const showPopoverExtras = useCallback(()=>{
        if (!isMounted) {
            return;
        }
        onVisible && onVisible();
    }, [
        isMounted,
        onVisible
    ]);
    const showPopover = useCallback(()=>{
        setIsVisible(true);
        showPopoverExtras();
    }, [
        showPopoverExtras
    ]);
    const hidePopoverExtras = useCallback(()=>{
        if (!isMounted) {
            return;
        }
        hoverTimerId.current && clearTimeout(hoverTimerId.current);
        wasOpenedByClick.current = false;
        onHide && onHide();
    }, [
        isMounted,
        onHide
    ]);
    const hidePopover = useCallback(()=>{
        hidePopoverExtras();
        setIsVisible(false);
    }, [
        hidePopoverExtras
    ]);
    const hidePopoverDebounced = useMemo(()=>throttle(hidePopover, SCROLL_THROTTLE_TIMEOUT, {
            leading: true
        }), [
        hidePopover
    ]);
    const togglePopover = useCallback(()=>setIsVisible((prevValue)=>{
            const willBeVisible = !prevValue;
            willBeVisible ? showPopoverExtras() : hidePopoverExtras();
            return willBeVisible;
        }), [
        showPopoverExtras,
        hidePopoverExtras
    ]);
    useEffect(()=>{
        if (!hideOnScroll || !isVisible) {
            return;
        }
        window.addEventListener('scroll', hidePopoverDebounced);
        return ()=>window.removeEventListener('scroll', hidePopoverDebounced);
    }, [
        hideOnScroll,
        hidePopoverDebounced,
        isVisible
    ]);
    const onClick = useCallback(()=>{
        if (!listenOnClicks) {
            return;
        }
        wasOpenedByClick.current = true;
        togglePopover();
    }, [
        listenOnClicks,
        togglePopover
    ]);
    const onMouseEnter = useCallback(()=>{
        if (!listenOnHover || wasOpenedByClick.current) {
            return;
        }
        hideTimerId.current && clearTimeout(hideTimerId.current);
        hoverTimerId.current = window.setTimeout(()=>showPopover(), hoverDelay);
    }, [
        listenOnHover,
        hoverDelay,
        showPopover
    ]);
    const onMouseLeave = useCallback(()=>{
        if (!listenOnHover || wasOpenedByClick.current) {
            return;
        }
        // a small delay on hiding is needed to handle both tooltip and reference hovers
        hideTimerId.current = window.setTimeout(()=>hidePopover(), 100);
        // clear timeout of any delayed hovers
        clearTimeout(hoverTimerId.current);
    }, [
        listenOnHover,
        hidePopover
    ]);
    const onClose = useCallback(()=>hidePopover(), [
        hidePopover
    ]);
    useEffect(()=>{
        return ()=>{
            hideTimerId.current && clearTimeout(hideTimerId.current);
            hoverTimerId.current && clearTimeout(hoverTimerId.current);
            hidePopoverDebounced.cancel();
            window.removeEventListener('wheel', hidePopoverDebounced);
        };
    }, [
        hidePopoverDebounced
    ]);
    return {
        onClick,
        onMouseEnter,
        onMouseLeave,
        onClose,
        isVisible
    };
}
